import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Menu } from 'antd';
import {
  BarChartOutlined,
  GlobalOutlined,
  HomeOutlined,
  ProjectOutlined,
  SettingOutlined,
  ShareAltOutlined,
  SwapOutlined,
  TeamOutlined,
  UserOutlined,
  CalendarOutlined,
  CarryOutOutlined,
  FileOutlined,
  FormOutlined,
  FieldTimeOutlined,
  ToolOutlined,
  ContactsOutlined,
  GoldOutlined,
  GroupOutlined,
  BoxPlotOutlined,
  ExperimentOutlined,
  TableOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  ProfileOutlined,
} from '@ant-design/icons';

import { useTranslation } from 'react-i18next';

import colors from '../constants/Colors';
import config from '../config';

import {
  ROUTE_TO_TEXT,
} from './menu.constants';

import Permissions from '../auth/Permissions';

import { getNumberOfAssignedAndDraftForms, getFormApprovalCount } from '../forms/formHelpers';
import IconPickerMap from '../common/IconPicker/iconPickerMap';

import { safeExtractFromObject } from '../helpers/helpers';

import OntraccrMenuItem from './OntraccrMenuItem';
import OntraccrSubMenu from './OntraccrSubMenu';
import useViewableCustomExports from '../reports/Exports/useViewableCustomExports';

const getDocumentsMenu = ({
  formCount = 0,
  routeMap,
}) => {
  const hasFormsPerms = Permissions.has('FORMS_READ');
  const badgeCount = hasFormsPerms ? formCount : 0;
  const hasFilePerms = Permissions.has('FILES_READ');
  const fileMenuItem = hasFilePerms ? (
    <OntraccrMenuItem
      key="files"
      text={routeMap.files}
      Icon={FileOutlined}
    />
  ) : null;
  if (!config.showForms) return fileMenuItem;
  if (!hasFormsPerms && !hasFilePerms) return null;
  return (
    <OntraccrSubMenu
      key="documents"
      text="Documents"
      Icon={FileOutlined}
      badgeCount={badgeCount}
    >
      {fileMenuItem}
      {hasFormsPerms && (
        <OntraccrMenuItem
          key="forms"
          text={routeMap.forms}
          Icon={FormOutlined}
          badgeCount={badgeCount}
        />
      )}
    </OntraccrSubMenu>
  );
};

const getScheduleMenu = (routeMap) => {
  if (!config.showGantt) {
    return (
      Permissions.match('SCHEDULE') && config.showSchedule && (
        <OntraccrMenuItem
          key="schedule"
          text="Scheduling"
          Icon={CalendarOutlined}
        />
      )
    );
  }
  return (
    (Permissions.match('PROJECT_GANTT_SCHEDULE_READ')
    || (Permissions.match('SCHEDULE') && config.showSchedule)) && (
      <OntraccrSubMenu
        key="scheduling"
        Icon={CalendarOutlined}
        text="Scheduling"
      >
        { Permissions.match('PROJECT_GANTT_SCHEDULE_READ') && (
          <OntraccrMenuItem
            key="gantt-schedule"
            text={routeMap['gantt-schedule']}
            level={2}
            Icon={BoxPlotOutlined}
          />
        )}
        { Permissions.match('SCHEDULE') && config.showSchedule && (
          <OntraccrMenuItem
            key="schedule"
            text={routeMap.schedule}
            level={2}
            Icon={CalendarOutlined}
          />
        )}
      </OntraccrSubMenu>
    )
  );
};

const getTimeCardMenu = ({ approvalCount, routeMap }) => (
  <OntraccrSubMenu
    key="timecards"
    Icon={FieldTimeOutlined}
    text="Time Cards"
    badgeCount={approvalCount}
  >
    { Permissions.has('TIMECARD_VIEW_SELF') && (
      <OntraccrMenuItem
        key="timecards/my"
        text={routeMap['timecards/my']}
        Icon={FieldTimeOutlined}
        level={2}
      />
    )}
    <OntraccrMenuItem
      key="timecards/team"
      text={routeMap['timecards/team']}
      Icon={CarryOutOutlined}
      badgeCount={approvalCount}
      level={2}
    />
  </OntraccrSubMenu>
);

export default function AppMenu({
  approvalCount,
  onOpenChange,
  onMenuPicked,
  selectedKeys,
  openKeys,
}) {
  const { t } = useTranslation();
  const allForms = useSelector((state) => state.forms);
  const formApprovals = useSelector((state) => state.forms.approvalSummary);
  const boardTags = useSelector((state) => state.boards.myCardTags);
  const projectTypes = useSelector((state) => state.projects.projectTypes);
  const bucketTemplates = useSelector((state) => state.buckets.bucketTemplates);
  const isMenuOpen = useSelector((state) => state.menu.isMenuOpen);
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const [viewableCustomExports] = useViewableCustomExports();
  const { paidFlags = [] } = useSelector((state) => state.settings.company) || {};

  const routeMap = ROUTE_TO_TEXT(t);

  const hasRedesign = settings?.uxRedesignPt1;

  const formCount = useMemo(() => (
    getFormApprovalCount(formApprovals) + getNumberOfAssignedAndDraftForms(allForms)
  ), [formApprovals, allForms]);

  const relevantBucketTemplates = useMemo(() => (
    bucketTemplates.filter(({ shouldShowPage }) => shouldShowPage)
  ), [bucketTemplates]);

  const hasProjectPermissions = Permissions.has('PROJECTS_WRITE')
    || Permissions.has('PROJECTS_VIEW_ALL')
    || Permissions.has('PROJECTS_VIEW_ASSIGNED');

  const showExportsMenu = viewableCustomExports.length > 0 || paidFlags.includes('AB_WORK_REPORTING');

  return (
    <Menu
      onOpenChange={onOpenChange}
      onClick={onMenuPicked}
      defaultSelectedKeys={[]}
      selectedKeys={selectedKeys}
      openKeys={openKeys}
      mode={hasRedesign && !isMenuOpen ? 'vertical' : 'inline'}
      theme="dark"
      style={{
        backgroundColor: hasRedesign
          ? colors.REDESIGN_PRIMARY_TEXT
          : colors.ONTRACCR_BLACK,
      }}
    >

      <OntraccrMenuItem
        key="dashboard"
        text={routeMap.dashboard}
        Icon={HomeOutlined}
      />

      <OntraccrSubMenu
        key="tasks"
        text="Tasks"
        Icon={ProfileOutlined}
      >
        <OntraccrMenuItem
          key="subtasks/my"
          text={routeMap['subtasks/my']}
          Icon={ProfileOutlined}
          level={2}
        />
        {Permissions.has('TASKS_OTHERS_READ') && (
          <OntraccrMenuItem
            key="subtasks/team"
            text={routeMap['subtasks/team']}
            Icon={TeamOutlined}
            level={2}
          />
        )}
      </OntraccrSubMenu>

      {getScheduleMenu(routeMap)}

      {getTimeCardMenu({ approvalCount, routeMap })}

      {(Permissions.match('USERS') || Permissions.has('TEAMS_WRITE') || Permissions.has('TEAMS_WRITE_ASSIGNED')) && (
        <OntraccrSubMenu
          key="staff"
          text="Staff"
          Icon={UserOutlined}
        >
          {Permissions.match('USERS') && (
            <OntraccrMenuItem
              key="users"
              text={`${Permissions.match('USERS') ? 'Manage' : 'View'} Users`}
              Icon={UserOutlined}
              level={2}
            />
          )}
          {(Permissions.has('TEAMS_WRITE') || Permissions.has('TEAMS_WRITE_ASSIGNED')) && (
            <OntraccrMenuItem
              key="teams"
              text={`${(Permissions.has('TEAMS_WRITE') || Permissions.has('TEAMS_WRITE_ASSIGNED')) ? 'Manage' : 'View'} Teams`}
              Icon={TeamOutlined}
              level={2}
            />
          )}
          {Permissions.has('USERS_MAP_READ') && (
            <OntraccrMenuItem
              key="users/map"
              text="User Map"
              Icon={EnvironmentOutlined}
              level={2}
            />
          )}
        </OntraccrSubMenu>
      )}

      <OntraccrSubMenu
        key="contacts"
        text="Contacts"
        Icon={ContactsOutlined}
        onMenuPicked={onMenuPicked}
      >
        {Permissions.match('CUSTOMERS') && (
          <OntraccrMenuItem
            key="contacts/customers"
            text={routeMap['contacts/customers']}
            Icon={ContactsOutlined}
            level={2}
          />
        )}
        {Permissions.match('VENDORS') && (
          <OntraccrMenuItem
            key="contacts/vendors"
            text={routeMap['contacts/vendors']}
            Icon={CarryOutOutlined}
            level={2}
          />
        )}
        {(Permissions.match('CUSTOMERS') || Permissions.match('VENDORS')) && (
          <OntraccrMenuItem
            key="contacts/addressbook"
            text={routeMap['contacts/addressbook']}
            Icon={ContactsOutlined}
            level={2}
          />
        )}
      </OntraccrSubMenu>

      { Permissions.has('BUCKETS_READ') && relevantBucketTemplates.map((bucketTemplate) => {
        const Icon = IconPickerMap[bucketTemplate.icon];
        return (
          <OntraccrMenuItem
            key={`buckets/${bucketTemplate.id}`}
            text={bucketTemplate.name}
            Icon={Icon}
          />
        );
      })}

      { hasProjectPermissions && (
        <OntraccrSubMenu
          key="projects-parent"
          text={t('Project', { count: 2 })}
          Icon={ProjectOutlined}
        >
          <OntraccrMenuItem
            key="projects"
            text={routeMap.projects}
            Icon={ProjectOutlined}
            level={2}
          />
          <OntraccrMenuItem
            key="projectGroups"
            text={routeMap.projectGroups}
            Icon={GroupOutlined}
            level={2}
          />
          {projectTypes.map((projectType) => {
            const Icon = IconPickerMap[projectType.icon];
            return (
              <OntraccrMenuItem
                key={`projects/type/${projectType.id}`}
                text={projectType.name}
                Icon={Icon}
                level={2}
              />
            );
          })}
        </OntraccrSubMenu>
      )}

      {(config.showPayablesPage
        && (Permissions.match('INVOICES') || Permissions.match('PURCHASE_ORDERS'))) && (
        <OntraccrMenuItem
          key="payables"
          text={routeMap.payables}
          Icon={DollarOutlined}
        />
      )}

      {Permissions.has('COSTCODES_WRITE') && (
        <OntraccrSubMenu
          key="costcode"
          text="Cost Codes"
          Icon={SwapOutlined}
        >
          <OntraccrMenuItem
            key="costcodes/global"
            text={routeMap['costcodes/global']}
            Icon={GlobalOutlined}
            level={2}
          />
          <OntraccrMenuItem
            key="costcodes/project-specific"
            text={routeMap['costcodes/project-specific']}
            Icon={ShareAltOutlined}
            level={2}
          />
        </OntraccrSubMenu>
      )}

      {Permissions.match('EQUIPMENT') && config.showEquipment && (
        <OntraccrMenuItem
          key="equipment"
          text={routeMap.equipment}
          Icon={ToolOutlined}
        />
      )}

      {Permissions.match('MATERIALS') && config.showMaterials && (
        <OntraccrMenuItem
          key="materials"
          text={routeMap.materials}
          Icon={GoldOutlined}
        />
      )}

      {Permissions.match('BOARDS') && config.showBoards && (
        <OntraccrMenuItem
          key="boards"
          text={routeMap.boards}
          Icon={GroupOutlined}
          badgeCount={boardTags.length}
        />
      )}

      {getDocumentsMenu({ formCount, routeMap })}

      {Permissions.match('REPORTS_VIEW') && (
        <OntraccrSubMenu
          key="reports-parent"
          text="Reports"
          Icon={BarChartOutlined}
        >
          <OntraccrMenuItem
            key="reports"
            text={routeMap.reports}
            Icon={BarChartOutlined}
            level={2}
          />
          {showExportsMenu && (
            <OntraccrMenuItem
              key="exports"
              text={routeMap.exports}
              Icon={TableOutlined}
              level={2}
            />
          )}
        </OntraccrSubMenu>
      )}

      {config.showAnalytics && Permissions.has('MANAGE_ANALYTICS') && (
        <OntraccrMenuItem
          key="analytics"
          text={routeMap.analytics}
          Icon={ExperimentOutlined}
        />
      )}

      {Permissions.has('SETTINGS_WRITE') && (
        <OntraccrMenuItem
          key="settings"
          text={routeMap.settings}
          Icon={SettingOutlined}
        />
      )}
    </Menu>
  );
}
